import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  BER_THONG_DEFAULT_VISUAL_THEME_ID,
  getBerThongVisualTheme,
} from '@huay/shared/ber-thong'
import { fetchBerThongPlayConfig, type BerThongSheet } from '../../services/berThongService'
import { BerThongCountdown } from './BerThongCountdown'
import { useBerThongNowMs } from './useBerThongNowMs'
import {
  formatCloseTimeCompact,
  isClosingSoon,
  isSheetBuyable,
  matchesHuayKey,
  sheetBetLabel,
} from './berThongUi'
import './berThongGame.css'

const BerThongSheetsPage: React.FC = () => {
  const navigate = useNavigate()
  const { huayKey: rawKey = '' } = useParams<{ huayKey: string }>()
  const huayKey = decodeURIComponent(rawKey)
  const [loading, setLoading] = useState(true)
  const [sheets, setSheets] = useState<BerThongSheet[]>([])
  const [betFilter, setBetFilter] = useState<'top2' | 'bottom2' | 'all'>('all')
  const nowMs = useBerThongNowMs()
  const [showClosed, setShowClosed] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchBerThongPlayConfig()
      setSheets(res.data?.sheets || [])
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'โหลดไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'ไม่สามารถโหลดแผ่น',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const huaySheets = useMemo(
    () => sheets.filter((s) => matchesHuayKey(s, huayKey)),
    [sheets, huayKey],
  )
  const huayName = huaySheets[0]?.huayName || 'หวย'

  const openSheets = useMemo(
    () => huaySheets.filter((s) => isSheetBuyable(s, nowMs)),
    [huaySheets, nowMs],
  )
  const closedSheets = useMemo(
    () => huaySheets.filter((s) => !isSheetBuyable(s, nowMs)),
    [huaySheets, nowMs],
  )

  useEffect(() => {
    if (betFilter !== 'all') return
    const top = openSheets.some((s) => s.betType === 'top2')
    const bottom = openSheets.some((s) => s.betType === 'bottom2')
    if (top && !bottom) setBetFilter('top2')
    else if (bottom && !top) setBetFilter('bottom2')
    else if (top) setBetFilter('top2')
  }, [openSheets, betFilter])

  const filteredOpen = useMemo(
    () => (betFilter === 'all' ? openSheets : openSheets.filter((s) => s.betType === betFilter)),
    [openSheets, betFilter],
  )
  const filteredClosed = useMemo(
    () => (betFilter === 'all' ? closedSheets : closedSheets.filter((s) => s.betType === betFilter)),
    [closedSheets, betFilter],
  )

  if (loading) {
    return <div className="htd-bt__loading">กำลังโหลดแผ่น...</div>
  }

  return (
    <div className="htd-bt">
      <div className="htd-bt__hero htd-bt__hero--anim">
        <div className="htd-bt__hero-copy">
          <Link to="/points/games/ber-thong" className="htd-bt__link">
            ← เลือกหวย
          </Link>
          <h2 className="htd-bt__title">{huayName}</h2>
          <p className="htd-bt__sub">เลือกแผ่นที่ยังรับแทง · กดเพื่อซื้อเบอร์</p>
        </div>
        <Link to="/history/games/ber-thong" className="htd-bt__link">
          ประวัติ
        </Link>
      </div>

      <div className="htd-bt__chips htd-bt__chips--bet" role="tablist" aria-label="ประเภท">
        {([
          ['top2', '2 ตัวบน'],
          ['bottom2', '2 ตัวล่าง'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`htd-bt__chip htd-bt__chip--soft${betFilter === key ? ' is-active' : ''}`}
            onClick={() => setBetFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {filteredOpen.length === 0 ? (
        <div className="htd-bt__empty">ยังไม่มีแผ่นเปิดขายในประเภทนี้</div>
      ) : (
        <div className="htd-bt__sheet-list">
          {filteredOpen.map((s, index) => {
            const soon = isClosingSoon(s.closeAt, nowMs)
            const theme = getBerThongVisualTheme(s.visualThemeId || BER_THONG_DEFAULT_VISUAL_THEME_ID)
            const remaining = Number(s.availableCount ?? 0)
            const price = Number(s.pricePerSlot || 0)
            const style = {
              ['--bt-i' as string]: Math.min(index, 8),
              ['--bt-accent' as string]: theme.accent,
            } as React.CSSProperties
            return (
              <button
                key={s.id}
                type="button"
                style={style}
                className={`htd-bt__sheet-card htd-bt__sheet-card--rich${soon ? ' is-urgent' : ''}${
                  theme.gridTone === 'dark' ? ' is-dark-theme' : ''
                }`}
                onClick={() => navigate(`/points/games/ber-thong/sheet/${s.id}`)}
              >
                <div className="htd-bt__sheet-visual" aria-hidden>
                  <span className="htd-bt__sheet-visual-glow" />
                  <span className="htd-bt__sheet-visual-name">{theme.name}</span>
                  <span className="htd-bt__sheet-visual-tag">{theme.tagline}</span>
                  <span className="htd-bt__sheet-visual-hint">ธีมแผ่น</span>
                </div>

                <div className="htd-bt__sheet-card-body">
                  <div className="htd-bt__sheet-card-top">
                    <strong className="htd-bt__sheet-title">{s.title}</strong>
                    <span className="htd-bt__sheet-cta">เลือกเบอร์</span>
                  </div>

                  <div className="htd-bt__sheet-chips">
                    <span className="htd-bt__sheet-chip">{sheetBetLabel(s)}</span>
                    {s.sheetCode ? <span className="htd-bt__sheet-chip htd-bt__sheet-chip--muted">{s.sheetCode}</span> : null}
                    {s.drawDate ? <span className="htd-bt__sheet-chip htd-bt__sheet-chip--muted">งวด {s.drawDate}</span> : null}
                  </div>

                  <div className="htd-bt__sheet-stats">
                    <div className="htd-bt__sheet-stat htd-bt__sheet-stat--price">
                      <small>ราคา/เบอร์</small>
                      <strong>{price.toLocaleString('th-TH')} ฿</strong>
                    </div>
                    <div className="htd-bt__sheet-stat htd-bt__sheet-stat--left">
                      <small>เบอร์เหลือ</small>
                      <strong>{remaining.toLocaleString('th-TH')}</strong>
                    </div>
                    {s.prizeLabel ? (
                      <div className="htd-bt__sheet-stat htd-bt__sheet-stat--prize">
                        <small>รางวัล</small>
                        <strong>{s.prizeLabel}</strong>
                      </div>
                    ) : null}
                  </div>

                  <div className="htd-bt__sheet-footer">
                    <BerThongCountdown closeAt={s.closeAt} />
                  </div>

                  {s.cancelIfBelowEnabled && s.minSoldToKeep ? (
                    <p className="htd-bt__sheet-rule">
                      ยกเลิกถ้าขายไม่ถึง {s.minSoldToKeep} เบอร์
                    </p>
                  ) : null}
                </div>
              </button>
            )
          })}
        </div>
      )}

      {filteredClosed.length > 0 ? (
        <div className="htd-bt__closed-block">
          <button
            type="button"
            className="htd-bt__closed-toggle"
            onClick={() => setShowClosed((v) => !v)}
          >
            {showClosed ? 'ซ่อน' : 'แสดง'} แผ่นปิดรับแล้ว ({filteredClosed.length})
          </button>
          {showClosed ? (
            <div className="htd-bt__sheet-list htd-bt__sheet-list--closed">
              {filteredClosed.map((s) => (
                <div key={s.id} className="htd-bt__sheet-card is-closed">
                  <div className="htd-bt__sheet-card-main">
                    <strong>{s.title}</strong>
                    <span>
                      {sheetBetLabel(s)} · ปิดรับแทงแล้ว
                      {s.closeAt ? ` · ${formatCloseTimeCompact(s.closeAt)}` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default BerThongSheetsPage
